import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * <AuthedImg src="/assets/xyz/qr.svg" ...imgProps />
 *
 * Plain <img> cannot send the Authorization header, so any backend endpoint
 * that requires a Bearer token returns 401 → broken image. This component
 * fetches the resource via our authenticated axios instance, converts to a
 * blob, and exposes it as an object URL. The URL is revoked on unmount and
 * whenever `src` changes to avoid memory leaks.
 *
 * `src` is a path relative to /api (e.g. "/assets/xyz/qr.svg") — matching
 * how the rest of the codebase uses `api.get(path)`.
 *
 * On error, calls `onError` (falls back to a broken-image state via alt text).
 */
export default function AuthedImg({ src, onError, ...imgProps }) {
    const [url, setUrl] = useState(null);
    const [errored, setErrored] = useState(false);
    const objectUrlRef = useRef(null);

    useEffect(() => {
        let alive = true;
        setErrored(false);
        setUrl(null);
        if (!src) return;
        api.get(src, { responseType: "blob" })
            .then((r) => {
                if (!alive) return;
                const obj = URL.createObjectURL(r.data);
                objectUrlRef.current = obj;
                setUrl(obj);
            })
            .catch((e) => {
                if (!alive) return;
                setErrored(true);
                if (onError) onError(e);
            });
        return () => {
            alive = false;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, [src, onError]);

    if (errored) return <img {...imgProps} src={TRANSPARENT_PIXEL} alt={imgProps.alt || "image unavailable"} data-authed-img="error" />;
    if (!url) return <img {...imgProps} src={TRANSPARENT_PIXEL} alt={imgProps.alt || ""} data-authed-img="loading" />;
    return <img {...imgProps} src={url} data-authed-img="ready" />;
}

// 1x1 transparent GIF — avoids React's "empty src" warning during load/error
// states while occupying the same layout footprint as the final image.
const TRANSPARENT_PIXEL =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
